import React, { useState, useEffect } from "react";
import Close from "../../utils/icons/Close";
import Tool from "../../utils/icons/Tool";
import { CLOUD_BUILD_OPERATIONS, ContentType } from "../../../constants";
import ContentSelector from "./ContentSelector";
import {
  closePopUp,
  showAlertPopUp,
  showLoadingPopUp,
} from "../../utils/Overlay";
import { saveContent } from "../../../apis/firebase";
import { createCloudBuildOp } from "../../../apis/firebase";
import OpDefault from "./operations/OpDefault";
import OpAudioVolume from "./operations/OpAudioVolume";
import OpAudioAndPhotoToVide from "./operations/OpAudioAndPhotoToVideo";
import OpCutVideo from "./operations/OpCutVideo";
import OpCutAudio from "./operations/OpCutAudio";
import OpPhotoManipulation from "./operations/OpPhotoManipulation";
import { getParameter, trackEvent } from "../../helpers";
import { Link } from "react-router-dom";

export default function Editor({
  post,
  onSuccess,
}: {
  post: Post;
  onSuccess: Function;
}) {
  const [
    selectedOperation,
    setOperation,
  ] = useState<CLOUD_BUILD_OPERATIONS | null>(null);

  useEffect(() => {
    const op = getParameter("op");
    const content = getParameter("content");

    if (op && content) {
      setOperation(op as CLOUD_BUILD_OPERATIONS);
    }
  }, []);

  useEffect(() => {
    if (selectedOperation !== null) {
      trackEvent(`editor_${selectedOperation}`);
    }
  }, [selectedOperation]);

  if (!post.content || post.content.length === 0) {
    return (
      <div className="tac">
        The editor works with your content. Please{" "}
        <Link to={`/campaigns/edit/${post.id}/content`}>add some</Link> first.
      </div>
    );
  }

  if (selectedOperation === CLOUD_BUILD_OPERATIONS.audio_photo_to_video) {
    return (
      <CreateJob
        post={post}
        title="Convert to video clip"
        text="Mix some of your images and an audio files to generate a video clip. Have in mind that this operation is time consuming."
        filter={[ContentSelector.filterAudio, ContentSelector.filterImage]}
        validator={ContentSelector.audioAndPhoto}
        operation={CLOUD_BUILD_OPERATIONS.audio_photo_to_video}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpAudioAndPhotoToVide}
        onSelected={async (data: EditorAudioAndPhotoToVideoData) => {
          const { content, filename, soundWave } = data;
          let newContent = await saveContent(post.id, {
            type: ContentType.FILE,
            actions: ["FILE_MANIPULATION"],
            value: filename,
            meta: {
              name: filename,
            },
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.audio_photo_to_video,
            payload: {
              postId: post.id,
              contentId: newContent.id as string,
              sourceContentIds: content.map((c) => c.id),
              soundWave,
            } as CloudBuildAudioAndPhotoToVideo,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.audio_audio_to_audio) {
    return (
      <CreateJob
        post={post}
        title="Merge audio files"
        text="Combine two or more audio files."
        filter={[ContentSelector.filterAudio]}
        validator={ContentSelector.audioAndAudio}
        operation={CLOUD_BUILD_OPERATIONS.audio_audio_to_audio}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        onSelected={async (data: EditorData) => {
          const { content, filename } = data;
          let newContent = await saveContent(post.id, {
            type: ContentType.FILE,
            actions: ["FILE_MANIPULATION"],
            value: filename,
            meta: {
              name: filename,
            },
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.audio_audio_to_audio,
            payload: {
              postId: post.id,
              contentId: newContent.id as string,
              sourceContentIds: content.map((c) => c.id),
            } as CloudBuildFilesTransformation,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.audio_volume) {
    return (
      <CreateJob
        post={post}
        title="Change volume up or down"
        text="Change the volume level of your audio files."
        buttonLabel="Change volume"
        filter={[ContentSelector.filterAudio]}
        validator={ContentSelector.singleAudio}
        operation={CLOUD_BUILD_OPERATIONS.audio_volume}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpAudioVolume}
        onSelected={async (data: EditorVolumeData) => {
          const { content, volume } = data;
          const c = content[0];
          await saveContent(post.id, {
            ...c,
            actions: ["FILE_MANIPULATION"],
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.audio_volume,
            payload: {
              postId: post.id,
              contentId: c.id as string,
              volume,
            } as CloudBuildVolume,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.video_volume) {
    return (
      <CreateJob
        post={post}
        title="Change volume up or down"
        text="Change the volume level of your video files."
        buttonLabel="Change volume"
        filter={[ContentSelector.filterVideo]}
        validator={ContentSelector.singleVideo}
        operation={CLOUD_BUILD_OPERATIONS.video_volume}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpAudioVolume}
        onSelected={async (data: EditorVolumeData) => {
          const { content, volume } = data;
          const c = content[0];
          await saveContent(post.id, {
            ...c,
            actions: ["FILE_MANIPULATION"],
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.video_volume,
            payload: {
              postId: post.id,
              contentId: c.id as string,
              volume,
            } as CloudBuildVolume,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.cut_video) {
    return (
      <CreateJob
        post={post}
        title="Trim a video clip"
        text="Cut a portion of existing video clip into a new one."
        buttonLabel="Cut"
        filter={[ContentSelector.filterVideo]}
        validator={ContentSelector.singleVideo}
        operation={CLOUD_BUILD_OPERATIONS.cut_video}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpCutVideo}
        onSelected={async (data: EditorCut) => {
          const { content, from, to, filename } = data;
          let newContent = await saveContent(post.id, {
            type: ContentType.FILE,
            actions: ["FILE_MANIPULATION"],
            value: filename,
            meta: {
              name: filename,
            },
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.cut_video,
            payload: {
              postId: post.id,
              contentId: newContent.id as string,
              sourceContentId: content[0].id as string,
              from,
              to,
            } as CloudBuildCut,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.cut_audio) {
    return (
      <CreateJob
        post={post}
        title="Trim an audio"
        text="Cut a portion of existing audio file into a new one."
        buttonLabel="Cut"
        filter={[ContentSelector.filterAudio]}
        validator={ContentSelector.singleAudio}
        operation={CLOUD_BUILD_OPERATIONS.cut_audio}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpCutAudio}
        onSelected={async (data: EditorCut) => {
          const { content, from, to, filename } = data;
          let newContent = await saveContent(post.id, {
            type: ContentType.FILE,
            actions: ["FILE_MANIPULATION"],
            value: filename,
            meta: {
              name: filename,
            },
          });
          createCloudBuildOp({
            operation: CLOUD_BUILD_OPERATIONS.cut_audio,
            payload: {
              postId: post.id,
              contentId: newContent.id as string,
              sourceContentId: content[0].id as string,
              from,
              to,
            } as CloudBuildCut,
          });
        }}
      />
    );
  }
  if (selectedOperation === CLOUD_BUILD_OPERATIONS.photo_manipulation) {
    return (
      <CreateJob
        post={post}
        title="Edit photo"
        text="Apply filter, crop, add text or shapes"
        buttonLabel="Save"
        filter={[ContentSelector.filterImage]}
        validator={ContentSelector.singleImage}
        operation={CLOUD_BUILD_OPERATIONS.photo_manipulation}
        onSuccess={onSuccess}
        onClose={() => setOperation(null)}
        form={OpPhotoManipulation}
        onSelected={async (data: EditorCut) => {}}
      />
    );
  }

  return (
    <div className="editor">
      <div>
        <h1 className="mb05">Photos</h1>
        <CreateJobButton
          title="Edit photo"
          text="Apply filter, crop, add text or shapes"
          onClick={() =>
            setOperation(CLOUD_BUILD_OPERATIONS.photo_manipulation)
          }
        />
        <CreateJobButton
          title="Convert photo + audio to a video clip"
          text="Mix some of your images and an audio files to generate a video clip. Have in mind that this operation is time consuming."
          onClick={() =>
            setOperation(CLOUD_BUILD_OPERATIONS.audio_photo_to_video)
          }
        />
        <h1 className="mb05">Audio</h1>
        <CreateJobButton
          title="Convert audio + photo file to a video clip"
          text="Mix some of your images and an audio files to generate a video clip. Have in mind that this operation is time consuming."
          onClick={() =>
            setOperation(CLOUD_BUILD_OPERATIONS.audio_photo_to_video)
          }
        />
        <CreateJobButton
          title="Merge audio files"
          text="Combine two or more audio files into one."
          onClick={() =>
            setOperation(CLOUD_BUILD_OPERATIONS.audio_audio_to_audio)
          }
        />
        <CreateJobButton
          title="Trim an audio"
          text="Cut a portion of existing audio file into a new one."
          onClick={() => setOperation(CLOUD_BUILD_OPERATIONS.cut_audio)}
        />
        <CreateJobButton
          title="Change volume up or down"
          text="Change the volume level of your audio file."
          onClick={() => setOperation(CLOUD_BUILD_OPERATIONS.audio_volume)}
        />
        <h1 className="mb05">Video</h1>
        <CreateJobButton
          title="Trim а video"
          text="Cut a portion of existing video clip into a new one."
          onClick={() => setOperation(CLOUD_BUILD_OPERATIONS.cut_video)}
        />
        <CreateJobButton
          title="Change volume up or down"
          text="Change the volume level of your video file."
          onClick={() => setOperation(CLOUD_BUILD_OPERATIONS.video_volume)}
        />
      </div>
    </div>
  );
}

function CreateJob({
  title,
  text,
  post,
  types,
  filter,
  validator,
  operation,
  onSuccess,
  onClose,
  buttonLabel,
  onSelected,
  form,
  preselectedContent,
}: {
  title: string;
  text: string;
  post: Post;
  types?: CONTENT_TYPE[];
  filter: Function[];
  validator: Function;
  operation: CLOUD_BUILD_OPERATIONS;
  onSuccess: Function;
  onClose: Function;
  buttonLabel?: string;
  onSelected: Function;
  form?: any;
  preselectedContent?: string[];
}) {
  types = types || [ContentType.FILE];
  buttonLabel = buttonLabel || "Merge";

  form = form || OpDefault;

  return (
    <div className="editor rel">
      <div className="feature">
        <h3 className="tac">{title}</h3>
        <p className="tac">
          <small>{text}</small>
        </p>
        <div className="mt1">
          <ContentSelector
            post={post}
            Form={form}
            single={
              operation === CLOUD_BUILD_OPERATIONS.audio_volume ||
              operation === CLOUD_BUILD_OPERATIONS.cut_video ||
              operation === CLOUD_BUILD_OPERATIONS.photo_manipulation
            }
            content={post.content as Content[]}
            labelButton={buttonLabel}
            types={types}
            filter={filter}
            validator={validator}
            onSelected={async (selection) => {
              showLoadingPopUp("Loading ...");
              try {
                await onSelected(selection);
                closePopUp();
                onSuccess();
              } catch (err) {
                console.error(err);
                showAlertPopUp("There is a problem saving the new file.");
              }
            }}
            cancel={onClose}
          />
        </div>
      </div>
      <button
        className="reset as-link abs"
        style={{ top: "1em", right: "1em" }}
        onClick={() => onClose()}
      >
        <Close />
      </button>
    </div>
  );
}

function CreateJobButton({
  title,
  text,
  onClick,
}: {
  title: string;
  text: string;
  onClick: Function;
}) {
  return (
    <div className="feature rel mb1">
      <div style={{ paddingRight: "6em" }}>
        <p>{title}</p>
        <small>{text}</small>
      </div>
      <button
        className="secondary block with-icon abs"
        onClick={() => onClick()}
        style={{ top: "1em", right: "1em" }}
      >
        <Tool size={16} /> Use
      </button>
    </div>
  );
}
